import { useCallback, useEffect, useState } from "react";
import { ApiError, type PortalSignableDocument } from "@dearbill/shared";
import { documentSlug, fetchDocuments } from "./documents";

export interface SignableDocumentState {
  doc: PortalSignableDocument | null;
  loadError: boolean;
  /** Replaces the document, e.g. with the `POST .../sign` response. */
  setDoc: (doc: PortalSignableDocument) => void;
  /** Re-reads the list and resolves to the fresh document, or null when it can't. */
  reload: () => Promise<PortalSignableDocument | null>;
}

/**
 * Loads one enrollment's signable documents and picks the one whose slug is
 * `slug` (`cms-1696` for `cms_1696`).
 *
 * A 401 calls `onSignOut`. A 404 (an enrollment outside the session's scope,
 * or an unknown id) and a slug that matches no document call `onExit`, so a
 * bad link lands on the dashboard rather than a dead end. Any other failure
 * sets `loadError`.
 */
export function useSignableDocument(
  enrollmentId: string,
  slug: string,
  session: string,
  onSignOut: () => void,
  onExit: () => void,
): SignableDocumentState {
  const [doc, setDoc] = useState<PortalSignableDocument | null>(null);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async (): Promise<PortalSignableDocument | null | "exit"> => {
    try {
      const docs = await fetchDocuments(enrollmentId, session);
      return docs.find((d) => documentSlug(d.documentId) === slug) ?? "exit";
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSignOut();
        return null;
      }
      if (err instanceof ApiError && err.status === 404) return "exit";
      throw err;
    }
  }, [enrollmentId, slug, session, onSignOut]);

  useEffect(() => {
    let cancelled = false;
    load()
      .then((found) => {
        if (cancelled) return;
        if (found === "exit") onExit();
        else if (found) setDoc(found);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [load, onExit]);

  const reload = useCallback(async (): Promise<PortalSignableDocument | null> => {
    try {
      const found = await load();
      if (found === "exit" || found === null) return null;
      setDoc(found);
      return found;
    } catch {
      return null;
    }
  }, [load]);

  return { doc, loadError, setDoc, reload };
}
